/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Alert, Form, Row, Col, Button, Typography } from 'antd';
import {
  CalculatorOutlined,
  ClearOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { withTranslation } from 'react-i18next';
import { withConfigContext } from '../../components/ConfigContext';
import PageLoading from '../../components/PageLoading';
import { Link } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';
import { URLParamsContext } from '../../index';
import axios from 'axios';
import styles from './styles.module.css';

import powericon from '/public/images/powericon.png';
import mobilelogo from '/public/images/logoonly.png';
import TariffSelectionCard from './components/FormCard/TariffSelectionCard';
import PeriodCard from './components/FormCard/PeriodCard';
import TotalImportExportCard from './components/FormCard/TotalImportExportCard';
import EnergyReadingsCard from './components/FormCard/EnergyReadingsCard';
import LecoBill from './components/LecoBill';
import { ENERGY_SECTIONS } from '../../services/utils/calculatorConstants';
import { handleApiError } from '../../services/utils/errorHandler';
import {
  validatePreviousDate,
  validateCurrentDate,
  validatePreviousReading,
  validateCurrentReading,
} from '../../services/utils/formValidators';
import moment from 'moment';
import { getVisibleCards } from './cardSelection';

const { Title } = Typography;

export const RoutesContext = React.createContext();

// ===== Helper Functions =====
const initializeDefaultTariff = (form, tariffData) => {
  if (!tariffData?.length) {
    return;
  }
  const defaultTariff = tariffData[1];
  form.setFieldsValue({
    tariffCode: defaultTariff.tariffCode,
    genTariffCode: defaultTariff.genTariffs?.[0]?.code,
  });
};

const getValidationRules = (fieldName, fieldType, comparisonPath, t) => {
  let baseRules = [];
  if (fieldType === 'date') {
    if (fieldName.includes('previous')) {
      baseRules = [
        { required: true, message: 'Please Enter Previous Reading Date' },
        validatePreviousDate(t),
      ];
    }
    if (fieldName.includes('current')) {
      baseRules = [
        { required: true, message: 'Please Enter Current Reading Date' },
        validateCurrentDate(t),
      ];
    }
  }
  if (fieldType === 'reading') {
    if (fieldName.includes('previous')) {
      baseRules = [
        { required: true, message: 'Please Enter Previous Meter Reading' },
        validatePreviousReading(comparisonPath, t),
      ];
    }
    if (fieldName.includes('current')) {
      baseRules = [
        { required: true, message: 'Please Enter Current Meter Reading' },
        validateCurrentReading(comparisonPath, t),
      ];
    }
  }
  return baseRules;
};

// ===== Main Component =====
const Home = ({ context, t }) => {
  const [loading, setLoading] = useState(false);
  const [tariffLoading, setTariffLoading] = useState(true);
  const [tariffError, setTariffError] = useState(false);
  const [tariffData, setTariffData] = useState([]);
  const [change, setChange] = useState(false);
  const [billData, setBillData] = useState(null);
  const [previousDate, setPreviousDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [selectedOption, setSelectedOption] = useState({
    period: 'Range',
    totalImport: 'Range',
    totalExport: 'Range',
    activeImport: 'Range',
    activeExport: 'Range',
    bulkImport: 'Range',
    bulkExport: 'Range',
  });

  // Get navbar visibility from URL params context
  const { showNavbar } = React.useContext(URLParamsContext);

  const [form] = Form.useForm();
  const tariffCode = Form.useWatch('tariffCode', form);
  const selectedTariff = tariffData?.find((t) => t.tariffCode === tariffCode);

  useEffect(() => {
    setTariffLoading(true);
    setTariffError(false);
    axios
      .get(
        window.location.origin +
          context.serverConfig.invoker.uri +
          '/api/bill-calculation/view-tariffs',
      )
      .then((res) => {
        setTariffData(res.data);
        setTariffLoading(false);
      })
      .catch(() => {
        setTariffLoading(false);
        setTariffError(true);
      });
  }, [context.serverConfig.invoker.uri]);

  // Initialize default tariff values
  useEffect(
    () => initializeDefaultTariff(form, tariffData),
    [tariffData, form],
  );

  // Compute visible cards
  const visibleCards = useMemo(
    () => getVisibleCards(selectedTariff?.registers),
    [selectedTariff],
  );

  const handleOptionChange = (cardKey, value) =>
    setSelectedOption((prev) => ({ ...prev, [cardKey]: value }));

  const billPreviewRef = useRef(null);
  const isMobile = window.innerWidth < 992;

  useEffect(() => {
    if (billData && isMobile) {
      billPreviewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [billData]);

  // ===== Form Submission =====
  const handleSubmit = async (values) => {
    setLoading(true);
    setChange(false);
    const request = covertToRequest(values);
    try {
      const response = await axios.post(
        `${window.location.origin}${context.serverConfig.invoker.uri}/api/bill-calculation`,
        request,
      );
      setBillData(response.data);
    } catch (error) {
      handleApiError(
        error,
        t('api_errorMsg'),
        t,
        error.response.data.detail.error,
      );
    } finally {
      setLoading(false);
    }
  };

  const covertToRequest = (formValues) => {
    const request = {
      billId: 'NJHMSJ1231',
      accountNo: '0300874904',
      tariffCategory: formValues.tariffCode,
      genTariffCategory: formValues.genTariffCode || '',
      readingOnTariffChangeAvailable: false,
      broughtForwards: [],
      readings: [],
    };
    let periodRange = {};
    if (formValues.period.NumOfDays) {
      const currentDate = moment().format('YYYY-MM-DD');
      const previousDate = moment()
        .subtract(formValues.period.NumOfDays, 'days')
        .format('YYYY-MM-DD');
      periodRange = {
        previousReadingDate: previousDate,
        currentReadingDate: currentDate,
      };
      setPreviousDate(previousDate);
      setCurrentDate(currentDate);
    } else {
      periodRange = {
        previousReadingDate:
          formValues.period.previousReadingDate.format('YYYY-MM-DD'),
        currentReadingDate:
          formValues.period.currentReadingDate.format('YYYY-MM-DD'),
      };
      setPreviousDate(
        formValues.period.previousReadingDate.format('YYYY-MM-DD'),
      );
      setCurrentDate(formValues.period.currentReadingDate.format('YYYY-MM-DD'));
    }
    if (formValues?.totalImport) {
      const totalImportReading =
        formValues.totalImport.numOfUnits != null
          ? {
              previousReading: 0,
              currentReading: formValues.totalImport.numOfUnits,
            }
          : formValues.totalImport;
      request.readings.push({
        meterSerial: '000014400006',
        registerValue: '1.8.0*01_0',
        meterStatus: 'Active',
        ...totalImportReading,
        ...periodRange,
        readingOnTariffChange: 0.0,
        tarriffChangeReadingDate: '',
        isMeterTurnOver: false,
      });
    }
    if (formValues?.totalExport) {
      const totalExportReading =
        formValues.totalExport.numOfUnits != null
          ? {
              previousReading: 0,
              currentReading: formValues.totalExport.numOfUnits,
            }
          : formValues.totalExport;
      request.readings.push({
        meterSerial: '000014400006',
        registerValue: '2.8.0*01_0',
        meterStatus: 'Active',
        ...totalExportReading,
        ...periodRange,
        readingOnTariffChange: 0.0,
        tarriffChangeReadingDate: '',
        isMeterTurnOver: false,
      });
    }
    if (formValues?.totalExport?.broughtForward) {
      request.broughtForwards.push({
        type: 'NetMetering',
        units: formValues.totalExport.broughtForward,
      });
    }
    if (formValues?.activeImport || formValues?.bulkImport) {
      if (
        formValues.activeImport?.dayTimeImport ||
        formValues.bulkImport?.dayTimeImport
      ) {
        const activeImportReading =
          formValues.activeImport?.dayTimeImport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.activeImport?.dayTimeImport.numOfUnits,
              }
            : formValues?.activeImport?.dayTimeImport;
        const bulkImportReading =
          formValues.bulkImport?.dayTimeImport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading: formValues.bulkImport.dayTimeImport.numOfUnits,
              }
            : formValues?.bulkImport?.dayTimeImport;
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '1.8.1*01_0',
          meterStatus: 'Active',
          ...(activeImportReading || bulkImportReading),
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
      if (
        formValues.activeImport?.peakTimeImport ||
        formValues.bulkImport?.peakTimeImport
      ) {
        const activeImportReading =
          formValues.activeImport?.peakTimeImport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.activeImport?.peakTimeImport.numOfUnits,
              }
            : formValues?.activeImport?.peakTimeImport;
        const bulkImportReading =
          formValues.bulkImport?.peakTimeImport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading: formValues.bulkImport.peakTimeImport.numOfUnits,
              }
            : formValues?.bulkImport?.peakTimeImport;
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '1.8.2*01_0',
          meterStatus: 'Active',
          ...(activeImportReading || bulkImportReading),
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
      if (
        formValues.activeImport?.offPeakTimeImport ||
        formValues.bulkImport?.offPeakTimeImport
      ) {
        const activeImportReading =
          formValues.activeImport?.offPeakTimeImport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.activeImport?.offPeakTimeImport.numOfUnits,
              }
            : formValues?.activeImport?.offPeakTimeImport;
        const bulkImportReading =
          formValues.bulkImport?.offPeakTimeImport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues?.bulkImport?.offPeakTimeImport.numOfUnits,
              }
            : formValues?.bulkImport?.offPeakTimeImport;
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '1.8.3*01_0',
          meterStatus: 'Active',
          ...(activeImportReading || bulkImportReading),
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
      if (formValues.bulkImport?.maximumDemand) {
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '9.6.0*01_0',
          meterStatus: 'Active',
          previousReading: 0,
          currentReading: formValues.bulkImport.maximumDemand.readings,
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
    }
    if (formValues?.activeExport || formValues?.bulkExport) {
      if (
        formValues.activeExport?.dayTimeExport ||
        formValues.bulkExport?.dayTimeExport
      ) {
        const activeExportReading =
          formValues.activeExport?.dayTimeExport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.activeExport?.dayTimeExport.numOfUnits,
              }
            : formValues?.activeExport?.dayTimeExport;
        const bulkExportReading =
          formValues.bulkExport?.dayTimeExport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading: formValues.bulkExport.dayTimeExport.numOfUnits,
              }
            : formValues?.bulkExport?.dayTimeExport;
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '2.8.1*01_0',
          meterStatus: 'Active',
          ...(activeExportReading || bulkExportReading),
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
      if (
        formValues.activeExport?.peakTimeExport ||
        formValues.bulkExport?.peakTimeExport
      ) {
        const activeExportReading =
          formValues.activeExport?.peakTimeExport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.activeExport?.peakTimeExport.numOfUnits,
              }
            : formValues?.activeExport?.peakTimeExport;
        const bulkExportReading =
          formValues.bulkExport?.peakTimeExport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading: formValues.bulkExport.peakTimeExport.numOfUnits,
              }
            : formValues?.bulkExport?.peakTimeExport;
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '2.8.2*01_0',
          meterStatus: 'Active',
          ...(activeExportReading || bulkExportReading),
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
      if (
        formValues.activeExport?.offPeakTimeExport ||
        formValues.bulkExport?.offPeakTimeExport
      ) {
        const activeExportReading =
          formValues.activeExport?.offPeakTimeExport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.activeExport?.offPeakTimeExport.numOfUnits,
              }
            : formValues?.activeExport?.offPeakTimeExport;
        const bulkExportReading =
          formValues.bulkExport?.offPeakTimeExport.numOfUnits != null
            ? {
                previousReading: 0,
                currentReading:
                  formValues.bulkExport.offPeakTimeExport.numOfUnits,
              }
            : formValues?.bulkExport?.offPeakTimeExport;
        request.readings.push({
          meterSerial: '000014400006',
          registerValue: '2.8.3*01_0',
          meterStatus: 'Active',
          ...(activeExportReading || bulkExportReading),
          ...periodRange,
          readingOnTariffChange: 0.0,
          tarriffChangeReadingDate: '',
          isMeterTurnOver: false,
        });
      }
      if (
        formValues?.activeExport?.broughtForward ||
        formValues?.bulkExport?.broughtForward
      ) {
        request.broughtForwards.push({
          type: 'NetMetering',
          units:
            formValues.activeExport?.broughtForward ||
            formValues.bulkExport?.broughtForward,
        });
      }
    }
    if (formValues?.activeExport) {
      const activeExportOffPeak =
        formValues.activeExport?.offPeakTimeExport.numOfUnits != null ??
        formValues.activeExport?.offPeakTimeExport?.currentReading -
          formValues.activeExport?.offPeakTimeExport?.previousReading;
      const activeExportPeak =
        formValues.activeExport?.peakTimeExport.numOfUnits != null ??
        formValues.activeExport?.peakTimeExport?.currentReading -
          formValues.activeExport?.peakTimeExport?.previousReading;
      const activeExportDay =
        formValues.activeExport?.dayTimeExport.numOfUnits != null ??
        formValues.activeExport?.dayTimeExport?.currentReading -
          formValues.activeExport?.dayTimeExport?.previousReading;
      request.readings.push({
        meterSerial: '000014400006',
        registerValue: '2.8.0*01_0',
        meterStatus: 'Active',
        currentReading:
          activeExportOffPeak + activeExportPeak + activeExportDay,
        previousReading: 0,
        ...periodRange,
        readingOnTariffChange: 0.0,
        tarriffChangeReadingDate: '',
        isMeterTurnOver: false,
      });
    }
    if (formValues?.bulkExport) {
      const bulkExportOffPeak =
        formValues.bulkExport?.offPeakTimeExport.numOfUnits != null ??
        formValues.bulkExport?.offPeakTimeExport?.currentReading -
          formValues.bulkExport?.offPeakTimeExport?.previousReading;
      const bulkExportPeak =
        formValues.bulkExport?.peakTimeExport.numOfUnits != null ??
        formValues.bulkExport?.peakTimeExport?.currentReading -
          formValues.bulkExport?.peakTimeExport?.previousReading;
      const bulkExportDay =
        formValues.bulkExport?.dayTimeExport.numOfUnits != null ??
        formValues.bulkExport?.dayTimeExport?.currentReading -
          formValues.bulkExport?.dayTimeExport?.previousReading;
      request.readings.push({
        meterSerial: '000014400006',
        registerValue: '2.8.0*01_0',
        meterStatus: 'Active',
        currentReading: bulkExportOffPeak + bulkExportPeak + bulkExportDay,
        previousReading: 0,
        ...periodRange,
        readingOnTariffChange: 0.0,
        tarriffChangeReadingDate: '',
        isMeterTurnOver: false,
      });
    }
    return request;
  };

  const handleReset = () => {
    form.resetFields();
    initializeDefaultTariff(form, tariffData);
    setBillData(null);
  };

  return (
    <div className={styles.applayoutNew}>
      {/* ===== Header ===== */}
      {showNavbar && (
        <Row className={styles.navRow} align="middle">
          <Col
            className={styles.topNavLogo}
            xs={24}
            sm={24}
            md={24}
            lg={10}
            xl={10}
            xxl={10}
          >
            <div className={styles.mobiletop}>
              <img src={mobilelogo} className={styles.mobilelogo} alt="logo" />
              <div>
                <h3 className={styles.mobiletxthead}>
                  {t('label_bill_calculator_title')}
                </h3>
                <span className={styles.mobilespanhead}>
                  {t('label_bill_calculator_sub_title')}
                </span>
              </div>
            </div>
            <div className={styles.logoContainer}>
              <img src={mobilelogo} alt="Logo" className={styles.logoImg} />
              <div className={styles.logoText}>
                <span className={styles.lecoText}>{t('label_leco')}</span>
                <span className={styles.companyName}>
                  {t('label_leco_name')}
                </span>
              </div>
            </div>
          </Col>
          <Col
            className={styles.topNavAction}
            xs={24}
            sm={24}
            md={24}
            lg={14}
            xl={14}
            xxl={14}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Link to={`/${context.appName}/tariff-breakdown`}>
                <Button
                  className={styles.backButton}
                  icon={<FileTextOutlined />}
                >
                  Tariff Breakdown
                </Button>
              </Link>
              <ThemeToggle t={t} />
              {/* <Button className={styles.howtoUse}>{t('label_how_to_use')}</Button>*/}
            </div>
          </Col>
        </Row>
      )}
      <Row className={styles.contentArea} gutter={16}>
        {/* ===== Form Area ===== */}
        <Col
          className={styles.filtersSection}
          xs={24}
          sm={24}
          md={24}
          lg={10}
          xl={10}
          xxl={10}
        >
          <Title level={2} className={styles.CalculatorTxt}>
            {t('label_bill_calculator_title')}
          </Title>
          <Title level={4} className={styles.mobilefilterheader}>
            {t('label_bill_calculator_mobile_sub_title')}
          </Title>

          <Form
            form={form}
            onValuesChange={(changedValues, allValues) => {
              if ('tariffCode' in changedValues) {
                form.resetFields();
                form.setFieldsValue({
                  tariffCode: changedValues.tariffCode,
                });
                setBillData(null);
              }
              if (!change) {
                setChange(true);
              }
            }}
            onFinish={handleSubmit}
            layout="horizontal"
          >
            {tariffLoading && <PageLoading message="Loading tariffs..." />}
            {tariffError && (
              <Alert
                type="error"
                showIcon
                message="Unable to load tariff categories."
              />
            )}
            <TariffSelectionCard
              t={t}
              form={form}
              tariffData={tariffData}
              selectedTariff={selectedTariff}
            />
            <PeriodCard
              t={t}
              form={form}
              selectedOption={selectedOption.period}
              onOptionChange={handleOptionChange}
              getValidationRules={getValidationRules}
            />
            {/* Render dynamic cards based on tariff */}
            {visibleCards.includes('TOTAL_IMPORT') && (
              <TotalImportExportCard
                t={t}
                form={form}
                cardType="totalImport"
                title={t('label_total_import')}
                subtitle={t('label_grid_to_customer')}
                selectedOption={selectedOption.totalImport}
                onOptionChange={handleOptionChange}
                getValidationRules={getValidationRules}
              />
            )}
            {visibleCards.includes('TOTAL_EXPORT') && (
              <TotalImportExportCard
                t={t}
                form={form}
                cardType="totalExport"
                title={t('label_total_export')}
                subtitle={t('label_customer_to_grid')}
                selectedOption={selectedOption.totalExport}
                onOptionChange={handleOptionChange}
                getValidationRules={getValidationRules}
                broughtForwards={selectedTariff?.hasBF}
              />
            )}
            {visibleCards.includes('ACTIVE_IMPORT') && (
              <EnergyReadingsCard
                t={t}
                form={form}
                cardType="activeImport"
                title={t('label_active_import_energy')}
                subtitle={t('label_grid_to_customer')}
                sections={ENERGY_SECTIONS.activeImportSections}
                selectedOption={selectedOption.activeImport}
                onOptionChange={handleOptionChange}
                getValidationRules={getValidationRules}
              />
            )}{' '}
            {visibleCards.includes('ACTIVE_EXPORT') && (
              <EnergyReadingsCard
                t={t}
                form={form}
                cardType="activeExport"
                title={t('label_active_export_energy')}
                subtitle={t('label_customer_to_grid')}
                sections={ENERGY_SECTIONS.activeExportSections}
                selectedOption={selectedOption.activeExport}
                onOptionChange={handleOptionChange}
                getValidationRules={getValidationRules}
                broughtForwards={selectedTariff.hasBF}
              />
            )}{' '}
            {visibleCards.includes('BULK_IMPORT') && (
              <EnergyReadingsCard
                t={t}
                form={form}
                cardType="bulkImport"
                title={t('label_import_energy')}
                subtitle={t('label_grid_to_customer')}
                sections={ENERGY_SECTIONS.bulkImportSections}
                selectedOption={selectedOption.bulkImport}
                getValidationRules={getValidationRules}
                onOptionChange={handleOptionChange}
              />
            )}{' '}
            {visibleCards.includes('BULK_EXPORT') && (
              <EnergyReadingsCard
                t={t}
                form={form}
                cardType="bulkExport"
                title={t('label_export_energy')}
                subtitle={t('label_customer_to_grid')}
                sections={ENERGY_SECTIONS.bulkExportSections}
                selectedOption={selectedOption.bulkExport}
                onOptionChange={handleOptionChange}
                getValidationRules={getValidationRules}
                broughtForwards={selectedTariff.hasBF}
              />
            )}
            <Row gutter={16} className={styles.padding20}>
              <Col
                className={styles.btnGroupNew}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <Button
                  className={styles.primaryBtn}
                  type="primary"
                  htmlType="submit"
                  icon={<CalculatorOutlined />}
                  loading={loading}
                  disabled={!change || tariffLoading || tariffError}
                >
                  {t('label_calculate_button')}
                </Button>
                <Button
                  className={styles.secondaryBtn}
                  onClick={handleReset}
                  icon={<ClearOutlined />}
                >
                  {t('label_clear')}
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>

        {/* ===== Bill Preview ===== */}
        <Col
          ref={billPreviewRef}
          className={`${styles.contentLoading} ${!isMobile ? styles.height100 : ''}`}
          xs={24}
          sm={24}
          md={24}
          lg={14}
          xl={14}
          xxl={14}
        >
          {loading ? (
            <div className={styles.landingmodelright}>
              <PageLoading message="Calculating bill..." />
            </div>
          ) : billData ? (
            <LecoBill
              billData={billData}
              previousDate={previousDate}
              currentDate={currentDate}
            />
          ) : (
            <div className={styles.landingmodelrightEmpty}>
              <div>
                <img
                  src={powericon}
                  className={styles.inconModel}
                  alt="powericon"
                />
              </div>
              <h3 className={styles.headerTxtlandingModel}>
                {t('label_ready_to_calculate')}
              </h3>
              <span className={styles.paralandigmodel}>
                {t('label_enter_your_details')}
              </span>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

Home.contextType = RoutesContext;
export default withConfigContext(withTranslation()(Home));
